import { AuthenticationClient } from '../authentication/client';
import { getUserId, isInternal, isTestUser, shouldDisableAnalytics } from '../authentication/utils';
import { applyMiddlewares } from './middlewares/middlewares';

let isMiddlewaresApplied = false;
let lastVerifiedUserId: string | undefined = undefined;
let useFakeAnalytics: boolean = false;

type Analytics = {
  track<T>(event: string, properties?: T): void;
  /**
   * @deprecated Use the 'track' function instead.
   */
  page(name: string, properties?: Object): void;
  identify(productName?: string, traits?: Object): void;
  group(): void;
};

type InternalAnalytics = {
  track(event: string, properties?: Object): void;
  /**
   * @deprecated Use the 'track' function instead.
   */
  page(name: string, properties?: Object): void;
  identify(userId: string, traits?: Object, integrations?: Object): void;
  group(groupId: number, traits?: Object): void;
};

const FakeAnalytics: InternalAnalytics = {
  track: (event: string, properties?: Object) => {},
  /**
   * @deprecated Use the 'track' function instead.
   */
  page: (name: string, properties?: Object) => {},
  identify: (userId: string, traits?: Object, integrations?: Object) => {},
  group: (groupId: number, traits?: Object) => {},
};

function shouldUseFakeAnalytics(): boolean {
  const user = AuthenticationClient.getInstance().getUser();
  const userId = getUserId(user?.userId);

  if (lastVerifiedUserId === userId) {
    return useFakeAnalytics;
  }

  lastVerifiedUserId = userId;

  useFakeAnalytics = !userId || isInternal(user) || isTestUser(user?.country) || shouldDisableAnalytics();

  return useFakeAnalytics;
}

function getAnalytics(): InternalAnalytics {
  if (shouldUseFakeAnalytics()) {
    return FakeAnalytics;
  }

  if (global['analytics']) {
    if (!isMiddlewaresApplied) {
      isMiddlewaresApplied = applyMiddlewares();
    }
    return global['analytics'] as Analytics;
  }

  return FakeAnalytics;
}

function track(event: string, properties: Object = {}): void {
  getAnalytics().track(event, properties);
}

/**
 * @deprecated Use the 'track' function instead.
 */
function page(name: string, properties: Object = {}): void {
  getAnalytics().page(name, properties);
}

function identify(productName?: string, traits?: Object): void {
  const user = AuthenticationClient.getInstance().getUser();
  if (!user) {
    throw new Error('No authenticated user to identify with');
  }

  getAnalytics().identify(user.userId, traits);

  // in the future we might disable integrations using identify like this:
  // const userId = getUserId(user?.userId);
  // const disableIntegrations = !userId || isGfkUser(userId) || isTestUser(user?.country) || shouldDisableAnalytics();
  // then either this:
  // global['analytics']?.identify(user.userId, traits, disableIntegrations ? { integrations: { All: false } } : undefined);
  // ... or that (which would require that getAnalytices does not return fake analytics):
  // getAnalytics().identify(user.userId, traits, disableIntegrations ? { integrations: { All: false } } : undefined);
}

function group(): void {
  const user = AuthenticationClient.getInstance().getUser();
  if (!user) {
    throw new Error('No authenticated user to group with');
  }

  if (!user.clientId) {
    throw new Error('Authenticated user does not have client info');
  }

  getAnalytics().group(user.clientId, {
    group_type: 'clientId',
    group_value: user.clientId,
    clientName: user.clientName,
  });
}

function useAnalytics(): Analytics {
  return {
    track,
    page,
    identify,
    group,
  };
}

export { useAnalytics };
